import styled from "styled-components";

import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import {
    ChangePercentage,
    DefaultCell,
    SearchKeywordCell,
    SimpleIconCell,
    TrafficShareWithVisits,
    UrlCellWebsiteAnalysis,
} from "app/components/React/Table/cells";
import { UrlCellContainer } from "app/components/React/Table/cells/UrlCell";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { NO_DATA_SIGN, THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { ACTIVATION_USERDATA_API } from "app/pages/activation/services/constants";
import type { ActivationSetSchema, ProjectsResponseSchema } from "app/pages/activation/types/api";
import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { AdColumnUnit } from "app/components/keyword-research/AdColumnUnit";
import {
    COLUMN_TITLES,
    TABLE_COLUMN_AD_DETAILS,
    TABLE_COLUMN_AD_TYPE,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_CLICKS_SHARE,
    TABLE_COLUMN_DOMAIN,
    TABLE_COLUMN_LANDING_PAGE,
    TABLE_COLUMN_TOP_KEYWORD,
    TABLE_COLUMN_VOLUME,
    TEXT,
} from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { TableTemplateV2 } from "../dashboard/TableTemplate-v2";
import type { AdData } from "../widget-fetchers/SearchAdsDataFetcherFactory";
import SearchAdsDataFetcherFactory, {
    adaptMultiIncludeExcludeRulesForUrl,
} from "../widget-fetchers/SearchAdsDataFetcherFactory";
import {
    INCLUDE_EXCLUDE_KEYWORDS,
    INCLUDE_EXCLUDE_TITLE,
    INCLUDE_EXCLUDE_URL,
} from "../widget-filters/components/consts";
import { SearchAdsTableWidgetFilters } from "../widget-filters/SearchAdsTableWidgetFilters";
import { WebsiteKeywordsTableTabSubtitle } from "../widget-subtitle-templates/WebsiteKeywordsTableTabSubtitle";

import { createSearchAdsWidgetFiltersMap, searchAdsTableWidgetAPIParams } from "./search-ads/searchAdsDashboardConsts";
import { getFiltersTooltip } from "./TableWebsiteKeywordsDashboardWidget";
import { TableWidget } from "./TableWidget";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

const COLUMN_CELL_CLASS = "pla-ads-ads-dashboard";

const UrlCellWrapper = styled.div`
    ${UrlCellContainer} {
        position: relative;
        width: unset;
    }
    .url-cell-content {
        display: flex;
    }
`;

interface IResponse {
    Data: AdData[];
    Filters: Record<string, any>;
    DomainsList: ActivationSetSchema[];
    TotalCount: number;
}
export class SearchAdsTableWidget extends TableWidget {
    public isPptSupported = () => {
        return false;
    };

    static getWidgetDashboardType() {
        return "Table";
    }

    static getWidgetResourceType() {
        return "Table";
    }

    static getWidgetMetadataType() {
        return "SearchAdsTable";
    }

    get templateUrl() {
        return `/app/components/widget/widget-templates/table.html`;
    }

    get templateComponent() {
        return TableTemplateV2;
    }

    protected getFetcherFactory() {
        return SearchAdsDataFetcherFactory;
    }

    static getFiltersComponent() {
        return SearchAdsTableWidgetFilters;
    }

    protected validateData(data: AdData[]) {
        return !!data?.length;
    }
    callbackOnGetData(response: IResponse) {
        response.Filters = response.DomainsList;
        super.callbackOnGetData(response);
    }
    async getViewData() {
        super.getViewData();

        const activationSets =
            (await DefaultFetchService.getInstance().get<ProjectsResponseSchema | null>(ACTIVATION_USERDATA_API))
                ?.activationSets ?? [];
        const searchAdsWidgetFiltersMap = createSearchAdsWidgetFiltersMap(activationSets);
        const mappedFilters = await Promise.all(
            searchAdsTableWidgetAPIParams.map((filter) => searchAdsWidgetFiltersMap[filter](this._params)),
        );

        this.viewData.subtitleTextItem = getFiltersTooltip(mappedFilters);
        this.viewData.customSubtitle = "true";
        this.viewData.customSubtitleComponent = WebsiteKeywordsTableTabSubtitle;
        return this.viewData;
    }

    public getProUrl() {
        const {
            orderBy = "clicksShare desc",
            adType,
            multiIncludeExcludeKeywords,
            multiIncludeExcludeUrl,
            multiIncludeExcludeTitle,
            domains,
        } = this._params;
        const finalParams = {
            ...this._getProUrlParams(),
            domains,
            orderBy,
            selectedPageTab: adType === TEXT ? TEXT : EAdsTabs[EAdsTabs.Product],
            ...this._params,
            ...(multiIncludeExcludeKeywords
                ? {
                      [INCLUDE_EXCLUDE_KEYWORDS]: JSON.stringify(
                          adaptMultiIncludeExcludeRulesForUrl(
                              this._params[INCLUDE_EXCLUDE_KEYWORDS],
                              multiIncludeExcludeType.KEYWORD,
                          ),
                      ),
                  }
                : {}),
            ...(multiIncludeExcludeUrl
                ? {
                      [INCLUDE_EXCLUDE_URL]: JSON.stringify(
                          adaptMultiIncludeExcludeRulesForUrl(
                              this._params[INCLUDE_EXCLUDE_URL],
                              multiIncludeExcludeType.URL,
                          ),
                      ),
                  }
                : {}),
            ...(multiIncludeExcludeTitle
                ? {
                      [INCLUDE_EXCLUDE_TITLE]: JSON.stringify(
                          adaptMultiIncludeExcludeRulesForUrl(
                              this._params[INCLUDE_EXCLUDE_TITLE],
                              multiIncludeExcludeType.TITLE,
                          ),
                      ),
                  }
                : {}),
        };
        return this._swNavigator.href(this._metricConfig.state, finalParams);
    }

    protected getMetricColumnsConfig() {
        const family = this._metricConfig.family;
        const isKeywordFamily = family === "Keyword";
        return [
            {
                name: TABLE_COLUMN_AD_TYPE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_AD_TYPE]),
                cellComponent: (props) => {
                    const {
                        adType: { iconDetails },
                        ...rest
                    } = props.row;
                    return (
                        <SimpleIconCell
                            row={{ iconDetails }}
                            {...(rest as ITableCellProps)}
                        />
                    );
                },
                width: 72,
            },
            {
                name: TABLE_COLUMN_AD_DETAILS,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_AD_DETAILS]),
                cellComponent: ({ value }) => (
                    <AdColumnUnit
                        {...value}
                        fixedImageHeight={false}
                    />
                ),
                width: 430,
            },
            {
                name: TABLE_COLUMN_CLICKS_SHARE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_CLICKS_SHARE]),
                cellComponent: ({ value: { totalClicks, value } }) => (
                    <TrafficShareWithVisits
                        row={{ totalClicks }}
                        value={value}
                        applyAbbrNumberFilter
                        undefinedSign={NO_DATA_SIGN}
                        totalVisitsRowProp="totalClicks"
                        thresholdForTotalFilter={50}
                        thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                        displayLessThanThreshold={true}
                    />
                ),
                width: 150,
            },
            {
                name: TABLE_COLUMN_CLICKS_CHANGE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_CLICKS_CHANGE]),
                cellComponent: ({ value, row }) => (
                    <ChangePercentage
                        value={value}
                        {...row}
                    />
                ),
                width: 88,
            },
            {
                name: TABLE_COLUMN_VOLUME,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_VOLUME]),
                cellComponent: ({ value, row }) => (
                    <DefaultCell
                        value={value}
                        {...row}
                    />
                ),
                width: isKeywordFamily ? 100 : 130,
            },
            {
                name: TABLE_COLUMN_TOP_KEYWORD,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_TOP_KEYWORD]),
                cellComponent: ({ value, row }) => (
                    <SearchKeywordCell
                        value={value}
                        withAdsLink={false}
                        navigatorParams={{
                            ...proNavigator.getParams(),
                            ...this._getProUrlParams(),
                            ...this.apiParams,
                        }}
                        {...row}
                    />
                ),
                width: isKeywordFamily ? 130 : 160,
            },
            {
                name: TABLE_COLUMN_LANDING_PAGE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_LANDING_PAGE]),
                cellComponent: ({ value, row }) => (
                    <UrlCellWrapper>
                        <UrlCellWebsiteAnalysis
                            value={value}
                            {...row}
                        />
                    </UrlCellWrapper>
                ),
                width: isKeywordFamily ? 150 : 180,
            },
            isKeywordFamily && {
                name: TABLE_COLUMN_DOMAIN,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_DOMAIN]),
                cellComponent: ({ value: { favicon, value } }) => {
                    const { isWWW, country, duration, webSource } = this.apiParams;
                    const props = {
                        domain: value,
                        icon: favicon,
                        internalLink: proNavigator.href("organicsearch_websites_v2_traffic_keywords", {
                            key: value,
                            country,
                            duration,
                            webSource,
                            isWWW,
                            selectedPageTab: EKeywordTabs[EKeywordTabs.Paid],
                        }),
                        trackInternalLink: (e) => {
                            e.stopPropagation();
                            allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${value}`);
                        },
                        externalLink: `http://${value}`,
                        trackExternalLink: (e) => {
                            e.stopPropagation();
                            allTrackers.dispatchTrackEvent("External Link", "click", `Table/${value}`);
                        },
                        hideTrackButton: true,
                        showToolTip: true,
                    };
                    return <CoreWebsiteCell {...props} />;
                },
                width: 152,
            },
        ]
            .filter(Boolean)
            .map((column) => ({ ...column, cellCls: COLUMN_CELL_CLASS }));
    }
}
SearchAdsTableWidget.register();
